import { Dispatch, SetStateAction } from 'react'
import axios from 'axios'

import { GOOGLE_MAPS_GEOCODING_KEY } from 'app/consts'
import { GOOGLE_GEOCODE_API, GOOGLE_MAP_SEARCH_LINK } from './const'
import type { GoogleMapLocation } from 'components/GoogleMaps/types'
import type { JobLocation } from 'features/jobs/types'

export const getGoogleMapLink = (latitude: number, longitude: number) =>
  `${GOOGLE_MAP_SEARCH_LINK}${latitude},${longitude}`

interface GoogleMapsDropdownOptionsProps {
  inputValue: string
  onError?: Dispatch<SetStateAction<undefined>>
  locale: string | undefined
}

export const loadGoogleMapsDropdownOptions = async ({
  inputValue,
  onError,
  locale,
}: GoogleMapsDropdownOptionsProps) => {
  if (!inputValue) {
    return []
  }
  try {
    const googleMapsData = await GoogleMapsDataFromAddress(inputValue, locale)
    const addresses = googleMapsData.results || []

    return addresses.map((address: GoogleMapLocation) => ({
      value: address,
      label: address.formatted_address,
    }))
  } catch (err) {
    if (onError) {
      onError(err)
    }
    console.log('Error uploading dropdown options from google maps', err)
  }
}

export const GoogleMapsDataFromAddress = async (
  address = '',
  language = 'en'
): Promise<{ results: GoogleMapLocation[] }> => {
  const googleMapsUrl = `${GOOGLE_GEOCODE_API}?address=${encodeURIComponent(
    address
  )}&key=${GOOGLE_MAPS_GEOCODING_KEY}&language=${language}`
  try {
    const response = await axios.get(googleMapsUrl)

    return response.data
  } catch (e) {
    throw new Error(`Error fetching google maps data for address - ${address}`)
  }
}

export const GoogleMapsDataFromLatLng = async (
  latitude: number,
  longitude: number,
  language = 'en'
): Promise<{ results: GoogleMapLocation[] }> => {
  const googleMapsUrl = `${GOOGLE_GEOCODE_API}?latlng=${encodeURIComponent(
    `${latitude},${longitude}`
  )}&key=${GOOGLE_MAPS_GEOCODING_KEY}&language=${language}`
  try {
    const response = await axios.get(googleMapsUrl)

    return response.data
  } catch (e) {
    throw new Error(
      `Error fetching google maps data for lat,lng - ${latitude},${longitude}`
    )
  }
}

export function getStreetNumber(
  addrComponents: GoogleMapLocation['address_components']
) {
  for (let i = 0; i < addrComponents.length; i += 1) {
    const addrComponentTypeIsStreet =
      addrComponents[i].types[0] === 'street_number'
    if (addrComponentTypeIsStreet) {
      return addrComponents[i].long_name
    }
  }
  return ' '
}

export function getRoute(
  addrComponents: GoogleMapLocation['address_components']
) {
  for (let i = 0; i < addrComponents.length; i += 1) {
    const addrComponentTypeIsRoute = addrComponents[i].types[0] === 'route'
    if (addrComponentTypeIsRoute) {
      return addrComponents[i].short_name
    }
  }
  return ' '
}

export function getSubpremise(
  addrComponents: GoogleMapLocation['address_components']
) {
  for (let i = 0; i < addrComponents.length; i += 1) {
    const addrComponentTypeIsSubpremise =
      addrComponents[i].types[0] === 'subpremise'
    if (addrComponentTypeIsSubpremise) {
      return addrComponents[i].short_name
    }
  }
  return ' '
}

export function getNeighborhood(
  addrComponents: GoogleMapLocation['address_components']
) {
  for (let i = 0; i < addrComponents.length; i += 1) {
    const addrComponentTypeIsNeighborhood =
      addrComponents[i].types[0] === 'neighborhood'
    const addrComponentTypeIsPolitical =
      addrComponents[i].types[0] === 'political'
    const addrComponentTypeLength = addrComponents[i].types.length === 2
    if (addrComponentTypeIsNeighborhood) {
      return addrComponents[i].short_name
    }
    if (addrComponentTypeLength && addrComponentTypeIsPolitical) {
      return addrComponents[i].long_name
    }
  }
  return ' '
}

export function getCity(
  addrComponents: GoogleMapLocation['address_components']
) {
  for (let i = 0; i < addrComponents.length; i += 1) {
    const addrComponentTypeIsLocality =
      addrComponents[i].types[0] === 'locality'
    const addrComponentTypeIsPolitical =
      addrComponents[i].types[0] === 'political'
    const addrComponentTypeLength = addrComponents[i].types.length === 2
    if (addrComponentTypeIsLocality) {
      return addrComponents[i].long_name
    }
    if (addrComponentTypeLength && addrComponentTypeIsPolitical) {
      return addrComponents[i].long_name
    }
  }
  return ' '
}

export function getState(
  addrComponents: GoogleMapLocation['address_components']
) {
  for (let i = 0; i < addrComponents.length; i += 1) {
    const addrComponentTypeIsAdministrativeArea =
      addrComponents[i].types[0] === 'administrative_area_level_1'
    const addrComponentTypeIsPolitical =
      addrComponents[i].types[0] === 'political'
    const addrComponentTypeLength = addrComponents[i].types.length === 2
    if (addrComponentTypeIsAdministrativeArea) {
      return addrComponents[i].short_name
    }
    if (addrComponentTypeLength && addrComponentTypeIsPolitical) {
      return addrComponents[i].short_name
    }
  }
  return ' '
}

export function getPostalCode(
  addrComponents: GoogleMapLocation['address_components']
) {
  for (let i = 0; i < addrComponents.length; i += 1) {
    const addrComponentTypeIsPostalCode =
      addrComponents[i].types[0] === 'postal_code'
    if (addrComponentTypeIsPostalCode) {
      return addrComponents[i].short_name
    }
  }
  return ' '
}

export function getCountry(
  addrComponents: GoogleMapLocation['address_components']
) {
  for (let i = 0; i < addrComponents.length; i += 1) {
    const addrComponentTypeIsCountry = addrComponents[i].types[0] === 'country'
    const addrComponentTypeIsPolitical =
      addrComponents[i].types[0] === 'political'
    const addrComponentTypeLength = addrComponents[i].types.length === 2
    if (addrComponentTypeIsCountry) {
      return addrComponents[i].short_name
    }
    if (addrComponentTypeLength && addrComponentTypeIsPolitical) {
      return addrComponents[i].short_name
    }
  }
  return ' '
}

export function getJobLocation(
  location: GoogleMapLocation,
  homeId: string | null = null,
  ipAddress: string | null = null
): JobLocation {
  const addressComponents = location.address_components
  const geometry = location.geometry

  const addressLine1 = `${getStreetNumber(addressComponents)} ${getRoute(
    addressComponents
  )} ${getSubpremise(addressComponents)} ${getNeighborhood(addressComponents)}`
  const addressLine2 = ' '
  const city = getCity(addressComponents)
  const state = getState(addressComponents)
  const postalCode = getPostalCode(addressComponents)
  const country = getCountry(addressComponents)
  const latitude = geometry.location.lat
  const longitude = geometry.location.lng

  return {
    address1: addressLine1,
    address2: addressLine2,
    city: city,
    country: country,
    homeId: homeId,
    ipAddress: ipAddress,
    state: state,
    postalCode: postalCode,
    latitude: latitude,
    longitude: longitude,
  }
}
